import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import ExcelJS from "exceljs";
import { jsPDF } from "jspdf";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";
import { useCapaAccess } from "./capaAccess";
import CapaListFilterDrawer from "./CapaListFilterDrawer";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import DateRangeFilter from "../../common/DateRangeFilter";
import Dropdown from "../../common/dropDown";
import ReportTable from "../../common/ReportTable/ReportTable";
import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import {
  capaModuleOptsFromDetail,
  fetchCapaEffectiveness,
  fetchCapaList,
  fetchCapaVerificationRequests,
} from "./capaService";
import { mapCapaStaffOptions } from "./capaStaffUtils";
import {
  CAPA_RISK_BADGE_BG,
  CAPA_RISK_OPTIONS,
  getCapaRiskBadgeStyle,
  getCapaStatusBadgeStyle,
} from "./capaConstants";
import {
  CAPA_LIST_FILTER_INITIAL,
  countActiveFilters,
  filterCapaRows,
} from "./capaListFilters";
import "./CapaReports.scss";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
);

const FIELD_OPTIONS = [
  { key: "capaId", label: "CAPA ID", type: "dimension" },
  { key: "title", label: "Title", type: "dimension" },
  { key: "stage", label: "Stage", type: "dimension" },
  { key: "type", label: "Type", type: "dimension" },
  { key: "assignedTo", label: "Assigned To", type: "dimension" },
  { key: "module", label: "Module", type: "dimension" },
  { key: "context", label: "Context", type: "dimension" },
  { key: "risk", label: "Risk", type: "dimension" },
  { key: "agingBucket", label: "Aging Bucket", type: "dimension" },
  { key: "overdueStatus", label: "Overdue Status", type: "dimension" },
  { key: "verificationOutcome", label: "Verification Outcome", type: "dimension" },
  { key: "effectivenessStatus", label: "Effectiveness Status", type: "dimension" },
  { key: "closureOutcome", label: "Closure Outcome", type: "dimension" },
  { key: "createdDate", label: "Created Date", type: "date" },
  { key: "closedDate", label: "Closed Date", type: "date" },
  { key: "dueDate", label: "Due Date", type: "date" },
  { key: "daysOpen", label: "Days Open", type: "number" },
  { key: "overdueDays", label: "Overdue Days", type: "number" },
];

const FILTER_INITIAL = {
  stage: "All",
  assignee: "All",
  risk: "All",
  type: "All",
  module: "All",
  context: "All",
  verificationOutcome: "All",
  effectivenessStatus: "All",
  closureOutcome: "All",
  dateRange: "This Month",
  dateFrom: "",
  dateTo: "",
};

const BUILDER_INITIAL = {
  rows: ["stage"],
  columns: [],
  values: [{ field: "capaId", agg: "count" }],
  filters: [],
};

const DEFAULT_TABLE_COLUMNS = ["capaId", "title", "stage", "risk", "assignedTo"];

const QUICK_REPORTS = [
  {
    key: "stage",
    category: "Pipeline",
    label: "CAPA by Stage",
    description: "Lifecycle distribution from open through closure.",
    apply: () => ({
      dataset: "capa",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["stage"],
        columns: [],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "open-capa",
    category: "Pipeline",
    label: "Open CAPA Register",
    description: "All CAPA records currently open for follow-up.",
    apply: () => ({
      dataset: "capa",
      canvasView: "table",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, stage: "Open", dateRange: "All Time" },
      builder: {
        rows: ["capaId"],
        columns: [],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "investigation-queue",
    category: "Pipeline",
    label: "Under Investigation",
    description: "CAPA records pending investigation completion.",
    apply: () => ({
      dataset: "capa",
      canvasView: "table",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, stage: "Under Investigation", dateRange: "All Time" },
      builder: {
        rows: ["assignedTo"],
        columns: ["risk"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "rca-queue",
    category: "Pipeline",
    label: "Under RCA",
    description: "Root cause analysis workload by owner and risk.",
    apply: () => ({
      dataset: "capa",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, stage: "Under RCA", dateRange: "All Time" },
      builder: {
        rows: ["assignedTo"],
        columns: ["risk"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "action-planning-stage",
    category: "Pipeline",
    label: "Action Planning Stage",
    description: "CAPA records that are in action planning before approval.",
    apply: () => ({
      dataset: "capa",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, stage: "Action Planning", dateRange: "All Time" },
      builder: {
        rows: ["assignedTo"],
        columns: ["risk"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "approved-awaiting-implementation",
    category: "Pipeline",
    label: "Approved Awaiting Implementation",
    description: "Approved CAPA records ready for implementation.",
    apply: () => ({
      dataset: "capa",
      canvasView: "table",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, stage: "Approved", dateRange: "All Time" },
      builder: {
        rows: ["assignedTo"],
        columns: ["risk"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "implementation-stage",
    category: "Pipeline",
    label: "Implementation Stage",
    description: "CAPA records currently in implementation.",
    apply: () => ({
      dataset: "capa",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, stage: "Implementation", dateRange: "All Time" },
      builder: {
        rows: ["assignedTo"],
        columns: ["overdueStatus"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "verification-stage",
    category: "Pipeline",
    label: "Verification Stage",
    description: "CAPA records ready for effectiveness or verification review.",
    apply: () => ({
      dataset: "capa",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, stage: "Verification", dateRange: "All Time" },
      builder: {
        rows: ["assignedTo"],
        columns: ["overdueStatus"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "closure-stage",
    category: "Pipeline",
    label: "Closure Stage",
    description: "CAPA records pending final closure activities.",
    apply: () => ({
      dataset: "capa",
      canvasView: "table",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, stage: "Closure", dateRange: "All Time" },
      builder: {
        rows: ["assignedTo"],
        columns: ["risk"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "closed-capa",
    category: "Pipeline",
    label: "Closed CAPA Register",
    description: "Closed CAPA records for audit and inspection readiness.",
    apply: () => ({
      dataset: "capa",
      canvasView: "table",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, stage: "Closed", dateRange: "All Time" },
      builder: {
        rows: ["closedDate"],
        columns: ["assignedTo"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "effective-closed",
    category: "Effectiveness & Closure",
    label: "Effectively Closed CAPA",
    description: "Closed CAPA records with an effective verification outcome.",
    apply: () => ({
      dataset: "capa",
      canvasView: "table",
      chartType: "bar",
      filters: {
        ...FILTER_INITIAL,
        closureOutcome: "Effectively Closed",
        dateRange: "All Time",
      },
      builder: {
        rows: ["assignedTo"],
        columns: ["risk"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "not-effective-closed",
    category: "Effectiveness & Closure",
    label: "Not Effectively Closed CAPA",
    description: "Closed CAPA records where verification was not effective.",
    apply: () => ({
      dataset: "capa",
      canvasView: "table",
      chartType: "bar",
      filters: {
        ...FILTER_INITIAL,
        closureOutcome: "Not Effectively Closed",
        dateRange: "All Time",
      },
      builder: {
        rows: ["assignedTo"],
        columns: ["risk"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "closure-outcome",
    category: "Effectiveness & Closure",
    label: "Closure Outcome Summary",
    description: "Effectively closed, not effectively closed, pending closure, and not closed.",
    apply: () => ({
      dataset: "capa",
      canvasView: "pivot",
      chartType: "pie",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["closureOutcome"],
        columns: ["risk"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "verification-outcome",
    category: "Effectiveness & Closure",
    label: "Verification Outcome Summary",
    description: "Effective, not effective, pending, and not reviewed outcomes.",
    apply: () => ({
      dataset: "capa",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["verificationOutcome"],
        columns: ["stage"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "effectiveness-pending",
    category: "Effectiveness & Closure",
    label: "Effectiveness Pending",
    description: "CAPA records waiting for effectiveness review or follow-up.",
    apply: () => ({
      dataset: "capa",
      canvasView: "table",
      chartType: "bar",
      filters: {
        ...FILTER_INITIAL,
        effectivenessStatus: "Effectiveness Pending",
        dateRange: "All Time",
      },
      builder: {
        rows: ["assignedTo"],
        columns: ["dueDate"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "effectiveness-by-owner",
    category: "Effectiveness & Closure",
    label: "Effectiveness by Owner",
    description: "Effectiveness status grouped by CAPA owner.",
    apply: () => ({
      dataset: "capa",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["assignedTo"],
        columns: ["effectivenessStatus"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "assignee",
    category: "Ownership",
    label: "CAPA by Assignee",
    description: "Workload by owner for capacity planning.",
    apply: () => ({
      dataset: "capa",
      canvasView: "chart",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["assignedTo"],
        columns: [],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "assignee-stage",
    category: "Ownership",
    label: "Assignee by Stage",
    description: "Owner workload split by current CAPA lifecycle stage.",
    apply: () => ({
      dataset: "capa",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["assignedTo"],
        columns: ["stage"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "assignee-risk",
    category: "Ownership",
    label: "Assignee by Risk",
    description: "Owner workload weighted by quality risk level.",
    apply: () => ({
      dataset: "capa",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["assignedTo"],
        columns: ["risk"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "task-by-assignee",
    category: "Ownership",
    label: "Pending Tasks by Assignee",
    description: "All pending workflow tasks grouped by person.",
    apply: () => ({
      dataset: "task",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["assignedTo"],
        columns: ["taskType"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "module",
    category: "Context",
    label: "CAPA by Module",
    description: "Compare CAPA volume across study, user, ISF, and other modules.",
    apply: () => ({
      dataset: "capa",
      canvasView: "chart",
      chartType: "pie",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["module"],
        columns: [],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "module-stage",
    category: "Context",
    label: "Module by Stage",
    description: "Lifecycle backlog by originating module.",
    apply: () => ({
      dataset: "capa",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["module"],
        columns: ["stage"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "context-owner",
    category: "Context",
    label: "Context by Owner",
    description: "Linked study, staff, or document contexts by responsible owner.",
    apply: () => ({
      dataset: "capa",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["context"],
        columns: ["assignedTo"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "source-type",
    category: "Context",
    label: "CAPA by Source Type",
    description: "Complaint, audit, deviation, and other CAPA source breakdown.",
    apply: () => ({
      dataset: "capa",
      canvasView: "chart",
      chartType: "pie",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["type"],
        columns: [],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "context",
    category: "Context",
    label: "CAPA by Context",
    description: "See records by study, staff, document, or linked business context.",
    apply: () => ({
      dataset: "capa",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["context"],
        columns: ["module"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "all-pending-tasks",
    category: "Pending Queues",
    label: "All Pending Workflow Tasks",
    description: "One queue for approval, implementation, verification, and action planning.",
    apply: () => ({
      dataset: "task",
      canvasView: "table",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["taskType"],
        columns: ["dueSchedule"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "pending-approval",
    category: "Pending Queues",
    label: "CAPA Pending Approval",
    description: "Records waiting in the approval handoff.",
    apply: () => ({
      dataset: "capa",
      canvasView: "table",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, stage: "Action Planning", dateRange: "All Time" },
      builder: {
        rows: ["assignedTo"],
        columns: ["stage"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "pending-implementation",
    category: "Pending Queues",
    label: "Pending Implementation Tasks",
    description: "Implementation work that still needs completion.",
    apply: () => ({
      dataset: "task",
      canvasView: "table",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, taskType: "Implementation", dateRange: "All Time" },
      builder: {
        rows: ["assignedTo"],
        columns: ["dueSchedule"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "pending-verification",
    category: "Pending Queues",
    label: "Pending Verification",
    description: "Verification requests awaiting evidence or decision.",
    apply: () => ({
      dataset: "task",
      canvasView: "table",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, taskType: "Verification", dateRange: "All Time" },
      builder: {
        rows: ["assignedTo"],
        columns: ["dueSchedule"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "pending-action-planning",
    category: "Pending Queues",
    label: "Pending Action Planning Items",
    description: "Action planning tasks across owners and due states.",
    apply: () => ({
      dataset: "task",
      canvasView: "table",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, taskType: "Action Planning", dateRange: "All Time" },
      builder: {
        rows: ["assignedTo"],
        columns: ["dueSchedule"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "pending-approval-tasks",
    category: "Pending Queues",
    label: "Pending Approval Requests",
    description: "Approval requests assigned to reviewers.",
    apply: () => ({
      dataset: "task",
      canvasView: "table",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, taskType: "Approval", dateRange: "All Time" },
      builder: {
        rows: ["assignedTo"],
        columns: ["dueSchedule"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "overdue",
    category: "SLA & Aging",
    label: "Overdue CAPA",
    description: "CAPA records past due, grouped by owner.",
    apply: () => ({
      dataset: "capa",
      canvasView: "table",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["assignedTo"],
        columns: [],
        values: [{ field: "capaId", agg: "count" }],
        filters: ["overdueDays"],
      },
    }),
  },
  {
    key: "overdue-status",
    category: "SLA & Aging",
    label: "Overdue vs On Track",
    description: "CAPA records split by overdue status.",
    apply: () => ({
      dataset: "capa",
      canvasView: "pivot",
      chartType: "pie",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["overdueStatus"],
        columns: ["stage"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "aging-bucket",
    category: "SLA & Aging",
    label: "CAPA Aging Buckets",
    description: "Open time bucketed into 0-7, 8-14, 15-30, 31-60, and 60+ days.",
    apply: () => ({
      dataset: "capa",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["agingBucket"],
        columns: ["stage"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "aging-by-risk",
    category: "SLA & Aging",
    label: "Aging by Risk",
    description: "Aging buckets grouped by risk for escalation meetings.",
    apply: () => ({
      dataset: "capa",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["agingBucket"],
        columns: ["risk"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "overdue-tasks",
    category: "SLA & Aging",
    label: "Overdue Task Queue",
    description: "All overdue approval, action, implementation, and verification work.",
    apply: () => ({
      dataset: "task",
      canvasView: "table",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dueSchedule: "overdue", dateRange: "All Time" },
      builder: {
        rows: ["taskType"],
        columns: ["assignedTo"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "aging-owner",
    category: "SLA & Aging",
    label: "Aging by Owner",
    description: "CAPA count by owner and aging bucket.",
    apply: () => ({
      dataset: "capa",
      canvasView: "chart",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["assignedTo"],
        columns: ["agingBucket"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "trend",
    category: "Trends",
    label: "Closure Trend",
    description: "Created/closed style trend foundation using created dates.",
    apply: () => ({
      dataset: "capa",
      canvasView: "chart",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "This Quarter" },
      builder: {
        rows: ["createdDate"],
        columns: [],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "created-trend",
    category: "Trends",
    label: "Created CAPA Trend",
    description: "CAPA volume by created date.",
    apply: () => ({
      dataset: "capa",
      canvasView: "chart",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "This Quarter" },
      builder: {
        rows: ["createdDate"],
        columns: ["module"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "closed-trend-owner",
    category: "Trends",
    label: "Closure Trend by Owner",
    description: "Closed CAPA review by closure date and owner.",
    apply: () => ({
      dataset: "capa",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, stage: "Closed", dateRange: "All Time" },
      builder: {
        rows: ["closedDate"],
        columns: ["assignedTo"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "due-date-trend",
    category: "Trends",
    label: "Due Date Trend",
    description: "Upcoming and historical CAPA due date distribution.",
    apply: () => ({
      dataset: "capa",
      canvasView: "chart",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["dueDate"],
        columns: ["overdueStatus"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "risk-owner",
    category: "Quality Risk",
    label: "Risk by Owner",
    description: "Quality risk portfolio by responsible CAPA owner.",
    apply: () => ({
      dataset: "capa",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["risk"],
        columns: ["assignedTo"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "risk-stage",
    category: "Quality Risk",
    label: "Risk by Stage",
    description: "High-risk CAPA exposure across workflow stages.",
    apply: () => ({
      dataset: "capa",
      canvasView: "pivot",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, dateRange: "All Time" },
      builder: {
        rows: ["risk"],
        columns: ["stage"],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "critical-risk-register",
    category: "Quality Risk",
    label: "Critical Risk CAPA Register",
    description: "Focused table for critical-risk CAPA follow-up.",
    apply: () => ({
      dataset: "capa",
      canvasView: "table",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, risk: "Critical", dateRange: "All Time" },
      builder: {
        rows: ["capaId"],
        columns: [],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
  {
    key: "high-risk-register",
    category: "Quality Risk",
    label: "High Risk CAPA Register",
    description: "Focused table for high-risk CAPA follow-up.",
    apply: () => ({
      dataset: "capa",
      canvasView: "table",
      chartType: "bar",
      filters: { ...FILTER_INITIAL, risk: "High", dateRange: "All Time" },
      builder: {
        rows: ["capaId"],
        columns: [],
        values: [{ field: "capaId", agg: "count" }],
        filters: [],
      },
    }),
  },
];

function toDate(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dateString(value) {
  const d = toDate(value);
  return d ? d.toLocaleDateString() : "—";
}

function daysBetween(a, b) {
  const x = toDate(a);
  const y = toDate(b);
  if (!x || !y) return 0;
  return Math.max(Math.ceil((y.getTime() - x.getTime()) / 86400000), 0);
}

function agingBucket(days) {
  const n = Number(days || 0);
  if (n <= 7) return "0-7 days";
  if (n <= 14) return "8-14 days";
  if (n <= 30) return "15-30 days";
  if (n <= 60) return "31-60 days";
  return "60+ days";
}

function buildOutcomeMeta(row, outcomeMap, effectivenessMap) {
  const capaId = String(row.CapaId || "");
  const capaNo = String(row.CapaNo || "");
  const verificationOutcome = outcomeMap.get(capaId) || "Not Reviewed";
  const effectivenessPending = effectivenessMap.has(capaNo) || effectivenessMap.has(capaId);
  let effectivenessStatus = verificationOutcome;
  if (effectivenessPending) effectivenessStatus = "Effectiveness Pending";
  if (verificationOutcome === "Not Reviewed") effectivenessStatus = "Not Reviewed";

  const status = String(row.Status || "").trim().toLowerCase();
  let closureOutcome = "Not Closed";
  if (status === "closure") {
    closureOutcome = "Pending Closure";
  } else if (status === "closed") {
    if (verificationOutcome === "Effective") closureOutcome = "Effectively Closed";
    else if (verificationOutcome === "Not Effective") closureOutcome = "Not Effectively Closed";
    else closureOutcome = "Closed - Outcome Not Recorded";
  }

  return { verificationOutcome, effectivenessStatus, closureOutcome };
}

function toCapaRecord(row, outcomeMap = new Map(), effectivenessMap = new Map()) {
  const status = row.Status || "Open";
  const outcomeMeta = buildOutcomeMeta(row, outcomeMap, effectivenessMap);
  const moduleValue = row.Module || "";
  const moduleLabel = row.ModuleLabel || row.Module || "Unassigned module";
  const contextLabel =
    row.ContextLabel ||
    row.EntityLabel ||
    row.StudyName ||
    row.DocumentName ||
    row.EntityId ||
    "No context";
  const contextValue = String(row.EntityId || contextLabel || "");
  const daysOpen =
    String(status).toLowerCase() === "closed"
      ? daysBetween(row.CreatedAt, row.ModifiedAt)
      : daysBetween(row.CreatedAt, new Date());
  const overdueDays = (() => {
    const due = toDate(row.TargetDueDate);
    if (!due) return 0;
    const end =
      String(status).toLowerCase() === "closed" ? toDate(row.ModifiedAt) : new Date();
    if (!end || end <= due) return 0;
    return daysBetween(due, end);
  })();
  return {
    dataset: "capa",
    capaId: row.CapaNo || "—",
    sourceId: String(row.CapaId || row.CapaNo || ""),
    title: row.Title || "—",
    stage: row.Status || "Open",
    status,
    type: row.SourceType || "General",
    ownerStaffId: row.OwnerStaffId || "",
    assignedTo: row.OwnerName || "—",
    moduleValue,
    module: moduleLabel,
    contextValue,
    context: contextLabel,
    risk: row.Risk || "Medium",
    taskType: "CAPA",
    dueSchedule: "record",
    createdAt: row.CreatedAt,
    createdDate: dateString(row.CreatedAt),
    closedDate: String(status).toLowerCase() === "closed" ? dateString(row.ModifiedAt) : "—",
    dueDate: dateString(row.TargetDueDate),
    daysOpen,
    overdueDays,
    agingBucket: agingBucket(daysOpen),
    overdueStatus: overdueDays > 0 ? "Overdue" : "On Track",
    verificationOutcome: outcomeMeta.verificationOutcome,
    effectivenessStatus: outcomeMeta.effectivenessStatus,
    closureOutcome: outcomeMeta.closureOutcome,
  };
}

function inDateRange(record, filterState) {
  const range =
    typeof filterState === "string" ? filterState : filterState?.dateRange;
  const from = typeof filterState === "string" ? "" : filterState?.dateFrom;
  const to = typeof filterState === "string" ? "" : filterState?.dateTo;
  if (range === "All Time" && !from && !to) return true;
  const d = toDate(record.createdAt || record.createdDate);
  if (!d) return false;

  if (from || to) {
    const start = from ? toDate(from) : null;
    const end = to ? toDate(to) : null;
    if (start) start.setHours(0, 0, 0, 0);
    if (end) end.setHours(23, 59, 59, 999);
    if (start && d < start) return false;
    if (end && d > end) return false;
    return true;
  }

  const now = new Date();
  const start = new Date(now);
  if (range === "Today") start.setHours(0, 0, 0, 0);
  if (range === "Yesterday") {
    start.setDate(now.getDate() - 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setHours(23, 59, 59, 999);
    return d >= start && d <= end;
  }
  if (range === "This Week") {
    start.setDate(now.getDate() - now.getDay());
    start.setHours(0, 0, 0, 0);
  }
  if (range === "Last Week") {
    const thisWeekStart = new Date(now);
    thisWeekStart.setDate(now.getDate() - now.getDay());
    thisWeekStart.setHours(0, 0, 0, 0);
    start.setTime(thisWeekStart.getTime());
    start.setDate(thisWeekStart.getDate() - 7);
    const end = new Date(thisWeekStart);
    end.setDate(thisWeekStart.getDate() - 1);
    end.setHours(23, 59, 59, 999);
    return d >= start && d <= end;
  }
  if (range === "Last 7 Days") start.setDate(now.getDate() - 7);
  if (range === "Last 15 Days") start.setDate(now.getDate() - 15);
  if (range === "Last 30 Days") start.setDate(now.getDate() - 30);
  if (range === "Last 90 Days") start.setDate(now.getDate() - 90);
  if (range === "This Month") {
    start.setMonth(now.getMonth(), 1);
    start.setHours(0, 0, 0, 0);
  }
  if (range === "Last Month") {
    start.setMonth(now.getMonth() - 1, 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    end.setHours(23, 59, 59, 999);
    return d >= start && d <= end;
  }
  if (range === "This Quarter") {
    start.setMonth(Math.floor(now.getMonth() / 3) * 3, 1);
    start.setHours(0, 0, 0, 0);
  }
  if (range === "Last Quarter") {
    const currentQuarterMonth = Math.floor(now.getMonth() / 3) * 3;
    start.setMonth(currentQuarterMonth - 3, 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(now.getFullYear(), currentQuarterMonth, 0);
    end.setHours(23, 59, 59, 999);
    return d >= start && d <= end;
  }
  if (range === "This Year") {
    start.setMonth(0, 1);
    start.setHours(0, 0, 0, 0);
  }
  if (range === "Last Year") {
    start.setFullYear(now.getFullYear() - 1, 0, 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(now.getFullYear() - 1, 11, 31);
    end.setHours(23, 59, 59, 999);
    return d >= start && d <= end;
  }
  return d >= start;
}

function fieldPassesBuilderFilter(row, field) {
  const value = row[field];
  const meta = getFieldMeta(field);
  if (meta?.type === "number") return Number(value || 0) > 0;
  return value != null && String(value).trim() !== "" && String(value).trim() !== "—";
}

function aggregate(values) {
  return values.length;
}

function getFieldMeta(key) {
  return FIELD_OPTIONS.find((f) => f.key === key);
}

const FALLBACK_CHART_COLORS = ["#2563eb", "#14b8a6", "#8b5cf6", "#f59e0b", "#ef4444", "#06b6d4"];
const REPORT_STAGE_OPTIONS = [
  { value: "Open", label: "Open" },
  { value: "Under Investigation", label: "Investigation" },
  { value: "Under RCA", label: "Under RCA" },
  { value: "Action Planning", label: "Action Planning" },
  { value: "Approved", label: "Approval" },
  { value: "Implementation", label: "Implementation" },
  { value: "Verification", label: "Verification" },
  { value: "Closure", label: "Closure" },
];

function colorFromMap(map, value) {
  const key = String(value || "").trim().toLowerCase();
  return map[key] || "";
}

function getReportChartColor(field, value, index) {
  if (field === "risk") {
    return colorFromMap(CAPA_RISK_BADGE_BG, value) || FALLBACK_CHART_COLORS[index % FALLBACK_CHART_COLORS.length];
  }
  if (field === "stage" || field === "status") {
    return getCapaStatusBadgeStyle(value).backgroundColor || FALLBACK_CHART_COLORS[index % FALLBACK_CHART_COLORS.length];
  }
  return FALLBACK_CHART_COLORS[index % FALLBACK_CHART_COLORS.length];
}

function renderTableCellValue(row, field) {
  const value = row[field] ?? "—";
  if (field === "stage") {
    return (
      <span className="badge rounded-pill capa-reports-builder__table-badge" style={getCapaStatusBadgeStyle(value)}>
        {String(value)}
      </span>
    );
  }
  if (field === "risk") {
    return (
      <span className="badge rounded-pill capa-reports-builder__table-badge" style={getCapaRiskBadgeStyle(value)}>
        {String(value)}
      </span>
    );
  }
  return String(value);
}

function downloadBlob(blob, filename) {
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function normalizeMultiFilterValue(value) {
  if (Array.isArray(value)) {
    return value.filter((item) => item && item !== "All");
  }
  if (value && value !== "All") return [value];
  return [];
}

const CapaReports = () => {
  const { userData, canCapaReports } = useCapaAccess();
  const [rawRows, setRawRows] = useState([]);
  const [verificationRows, setVerificationRows] = useState([]);
  const [effectivenessRows, setEffectivenessRows] = useState([]);
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [filters, setFilters] = useState(FILTER_INITIAL);
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedListFilters, setAppliedListFilters] = useState(CAPA_LIST_FILTER_INITIAL);
  const [draftListFilters, setDraftListFilters] = useState(CAPA_LIST_FILTER_INITIAL);
  const [builder, setBuilder] = useState(BUILDER_INITIAL);
  const [canvasView, setCanvasView] = useState("pivot");
  const [chartType, setChartType] = useState("bar");
  const [activePresetKey, setActivePresetKey] = useState("stage");
  const [presetSearch, setPresetSearch] = useState("");
  const [showFiltersPanel, setShowFiltersPanel] = useState(true);
  const chartRef = useRef(null);

  const ownerFallbackName = [userData?.FirstName, userData?.LastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await fetchCapaList(userData);
      const [verificationResults, effectivenessResults] = await Promise.all([
        Promise.all(
          list.map(async (row) => {
            const requests = await fetchCapaVerificationRequests(
              row.CapaId,
              userData,
              capaModuleOptsFromDetail(row, userData),
            );
            return requests.map((request) => ({
              ...request,
              CapaId: request.CapaId || row.CapaId,
              CapaNo: request.CapaNo || row.CapaNo,
            }));
          }),
        ),
        fetchCapaEffectiveness({}),
      ]);
      if (!cancelled) {
        setRawRows(list);
        setVerificationRows(verificationResults.flat());
        setEffectivenessRows(effectivenessResults || []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData]);

  const loadOwnerOptions = useCallback(async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;
    const response = await api.post(API_ENDPOINTS.USER.GET_USERLIST, {
      OrganizationId: userData.OrganizationId,
      StaffId: userData.StaffId,
      Action: "Get",
      AccessCode: "",
      Password: "",
      HaveProfileViewRights: 1,
      HaveProfileEditRights: 0,
      HaveDelegationLogViewRights: 1,
      HaveDelegationLogEditRights: 0,
      HavingLineManagerRights: 1,
      HaveStudyTaskArchiveRights: 0,
      CreatedBy: userData.StaffId,
    });
    if (response?.success === 1) {
      const list = response.data?.UsersActivity || [];
      setOwnerOptions(mapCapaStaffOptions(list));
    }
  }, [userData]);

  useEffect(() => {
    loadOwnerOptions();
  }, [loadOwnerOptions]);

  const activeListFilterCount = useMemo(
    () => countActiveFilters(appliedListFilters),
    [appliedListFilters],
  );

  const scopedRawRows = useMemo(
    () => filterCapaRows(rawRows, appliedListFilters, ownerFallbackName, userData),
    [rawRows, appliedListFilters, ownerFallbackName, userData],
  );

  const verificationOutcomeMap = useMemo(() => {
    const map = new Map();
    verificationRows.forEach((row) => {
      const status = String(row.RequestStatus || "").trim().toLowerCase();
      const outcome = String(row.Outcome || "").trim();
      let value = "";
      if (status === "pending") value = "Pending Verification";
      if (status === "submitted" && outcome) value = outcome;
      if (!value) return;
      const key = String(row.CapaId || "");
      const existing = map.get(key);
      if (value === "Not Effective" || !existing || existing === "Not Reviewed") {
        map.set(key, value);
      } else if (value === "Effective" && existing !== "Not Effective") {
        map.set(key, value);
      }
    });
    return map;
  }, [verificationRows]);

  const effectivenessMap = useMemo(() => {
    const map = new Map();
    effectivenessRows.forEach((row) => {
      const status = String(row.Status || "").trim().toLowerCase();
      if (status && status !== "pending") return;
      if (row.CapaNo) map.set(String(row.CapaNo), row);
      if (row.CapaId) map.set(String(row.CapaId), row);
    });
    return map;
  }, [effectivenessRows]);

  const capaRecords = useMemo(
    () => scopedRawRows.map((row) => toCapaRecord(row, verificationOutcomeMap, effectivenessMap)),
    [scopedRawRows, verificationOutcomeMap, effectivenessMap],
  );

  const reportRows = capaRecords;

  const options = useMemo(() => {
    const stages = REPORT_STAGE_OPTIONS;
    const fallbackOwners = Array.from(
      new Map(
        reportRows
          .map((row) => [
            String(row.ownerStaffId || row.assignedTo || ""),
            {
              value: String(row.ownerStaffId || row.assignedTo || ""),
              label: row.assignedTo || "—",
            },
          ])
          .filter(([value]) => value && value !== "—"),
      ).values(),
    ).sort((a, b) => String(a.label).localeCompare(String(b.label)));
    const assignees = ownerOptions.length ? ownerOptions : fallbackOwners;
    const risks = CAPA_RISK_OPTIONS;
    const types = Array.from(
      new Map(
        reportRows
          .map((row) => String(row.type || "").trim())
          .filter(Boolean)
          .map((v) => [v, { value: v, label: v }]),
      ).values(),
    );
    const modulesFromRows = Array.from(
      new Map(
        reportRows.map((row) => [
          String(row.moduleValue || row.module || "").trim(),
          {
            value: String(row.moduleValue || row.module || "").trim(),
            label: String(row.module || row.moduleValue || "").trim(),
          },
        ]),
      ).values(),
    ).filter((option) => option.value);
    const modules = [{ value: "All", label: "All modules" }, ...modulesFromRows];
    const selectedModule = String(filters.module || "All");
    const contextSourceRows =
      selectedModule === "All"
        ? []
        : reportRows.filter(
            (row) =>
              row.moduleValue === selectedModule ||
              row.module === selectedModule,
          );
    const ownerLabelById = new Map(ownerOptions.map((owner) => [String(owner.value), owner.label]));
    const contexts = Array.from(
      new Map(
        contextSourceRows
          .map((row) => {
            const value = String(row.contextValue || row.context || "");
            const label =
              selectedModule === "user_management"
                ? ownerLabelById.get(value) || row.context
                : row.context;
            return [value, { value, label }];
          })
          .filter(([value]) => value && value !== "No context"),
      ).values(),
    ).sort((a, b) => String(a.label).localeCompare(String(b.label)));
    const verificationOutcomes = Array.from(
      new Set(reportRows.map((r) => r.verificationOutcome)),
    ).sort();
    const effectivenessStatuses = Array.from(
      new Set(reportRows.map((r) => r.effectivenessStatus)),
    ).sort();
    const closureOutcomes = Array.from(
      new Set(reportRows.map((r) => r.closureOutcome)),
    ).sort();
    return {
      stages,
      assignees,
      risks,
      types,
      modules,
      contexts,
      verificationOutcomes,
      effectivenessStatuses,
      closureOutcomes,
    };
  }, [filters.module, ownerOptions, reportRows]);

  const selectedStages = useMemo(
    () => normalizeMultiFilterValue(filters.stage),
    [filters.stage],
  );

  const selectedRisks = useMemo(
    () => normalizeMultiFilterValue(filters.risk),
    [filters.risk],
  );

  const selectedOwners = useMemo(
    () => normalizeMultiFilterValue(filters.assignee),
    [filters.assignee],
  );

  const selectedSourceTypes = useMemo(
    () => normalizeMultiFilterValue(filters.type),
    [filters.type],
  );

  const selectedModule = useMemo(
    () =>
      options.modules.find((option) => option.value === filters.module) ||
      options.modules[0],
    [filters.module, options.modules],
  );

  const selectedContexts = useMemo(() => {
    const ids = new Set(normalizeMultiFilterValue(filters.context).map(String));
    return options.contexts.filter((option) => ids.has(String(option.value)));
  }, [filters.context, options.contexts]);

  const contextPlaceholderOption = useMemo(
    () => ({
      value: "",
      label: filters.module === "All" ? "Select module first" : "Select contexts",
    }),
    [filters.module],
  );

  const filteredRows = useMemo(() => {
    return reportRows.filter((row) => {
      if (!inDateRange(row, filters)) return false;
      const stageFilters = normalizeMultiFilterValue(filters.stage);
      if (stageFilters.length && !stageFilters.includes(row.stage)) return false;
      const ownerFilters = normalizeMultiFilterValue(filters.assignee);
      if (
        ownerFilters.length &&
        !ownerFilters.includes(String(row.ownerStaffId || "")) &&
        !ownerFilters.includes(row.assignedTo)
      ) return false;
      const riskFilters = normalizeMultiFilterValue(filters.risk);
      if (riskFilters.length && !riskFilters.includes(row.risk)) return false;
      const sourceTypeFilters = normalizeMultiFilterValue(filters.type);
      if (sourceTypeFilters.length && !sourceTypeFilters.includes(row.type)) return false;
      if (
        filters.module !== "All" &&
        row.moduleValue !== filters.module &&
        row.module !== filters.module
      ) return false;
      const contextFilters = normalizeMultiFilterValue(filters.context);
      if (
        contextFilters.length &&
        !contextFilters.includes(String(row.contextValue || "")) &&
        !contextFilters.includes(row.context)
      ) return false;
      if (
        filters.verificationOutcome !== "All" &&
        row.verificationOutcome !== filters.verificationOutcome
      ) return false;
      if (
        filters.effectivenessStatus !== "All" &&
        row.effectivenessStatus !== filters.effectivenessStatus
      ) return false;
      if (
        filters.closureOutcome !== "All" &&
        row.closureOutcome !== filters.closureOutcome
      ) return false;
      if (!builder.filters.every((field) => fieldPassesBuilderFilter(row, field))) return false;
      return true;
    });
  }, [reportRows, filters, builder.filters]);

  const reportSummary = useMemo(() => {
    const uniqueCapas = new Set(filteredRows.map((r) => r.capaId)).size;
    const overdue = filteredRows.filter((r) => Number(r.overdueDays || 0) > 0).length;
    const owners = new Set(filteredRows.map((r) => r.assignedTo)).size;
    return [
      { label: "Rows", value: filteredRows.length },
      { label: "Unique CAPA", value: uniqueCapas },
      { label: "Owners", value: owners },
      { label: "Overdue CAPA", value: overdue },
    ];
  }, [filteredRows]);

  const pivot = useMemo(() => {
    const rowField = builder.rows[0];
    const colField = builder.columns[0];
    const value = builder.values[0] || { field: "capaId", agg: "count" };
    if (!rowField) return { rowKeys: [], colKeys: [], matrix: new Map(), value };

    const rowKeys = Array.from(new Set(filteredRows.map((r) => String(r[rowField] || "—"))));
    const colKeys = colField
      ? Array.from(new Set(filteredRows.map((r) => String(r[colField] || "—"))))
      : ["Total"];

    const matrix = new Map();
    rowKeys.forEach((rk) => {
      const inner = new Map();
      colKeys.forEach((ck) => {
        const scoped = filteredRows.filter((r) => {
          const rowMatch = String(r[rowField] || "—") === rk;
          const colMatch = colField ? String(r[colField] || "—") === ck : true;
          return rowMatch && colMatch;
        });
        inner.set(ck, aggregate(scoped.map((s) => s[value.field])));
      });
      matrix.set(rk, inner);
    });
    return { rowKeys, colKeys, matrix, value };
  }, [filteredRows, builder]);

  const chartData = useMemo(() => {
    const labels = pivot.rowKeys;
    const rowField = builder.rows[0];
    const values = labels.map((rk) => {
      const rowMap = pivot.matrix.get(rk);
      return (pivot.colKeys || []).reduce((sum, ck) => sum + Number(rowMap?.get(ck) || 0), 0);
    });
    const colors = labels.map((label, index) => getReportChartColor(rowField, label, index));
    return { labels, values, colors };
  }, [pivot, builder.rows]);

  const barChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 6, right: 12, bottom: 4, left: 0 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#0f172a",
          padding: 10,
          titleFont: { size: 12, weight: "700" },
          bodyFont: { size: 12 },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: "#475569",
            font: { size: 11, weight: "600" },
            maxRotation: 0,
          },
        },
        y: {
          beginAtZero: true,
          grid: { color: "#e5e7eb" },
          ticks: {
            color: "#64748b",
            precision: 0,
            font: { size: 11 },
          },
        },
      },
    }),
    [],
  );

  const pieChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            boxWidth: 10,
            boxHeight: 10,
            color: "#475569",
            font: { size: 11, weight: "600" },
            padding: 14,
          },
        },
        tooltip: {
          backgroundColor: "#0f172a",
          padding: 10,
          titleFont: { size: 12, weight: "700" },
          bodyFont: { size: 12 },
        },
      },
    }),
    [],
  );

  const activePreset = QUICK_REPORTS.find((q) => q.key === activePresetKey);

  const exportRowsToExcel = useCallback(async (rows, columns, filename, worksheetName = "CAPA Report") => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(worksheetName);
    const headers = columns.map((field) => getFieldMeta(field)?.label || field);
    const headerRow = ws.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF4472C4" },
      };
    });

    rows.forEach((row) => {
      ws.addRow(columns.map((field) => String(row[field] ?? "")));
    });

    ws.columns = headers.map((header) => ({
      header,
      key: header,
      width: Math.max(14, Math.min(32, header.length + 8)),
    }));

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    downloadBlob(blob, filename);
  }, []);

  const exportTableToExcel = useCallback(async ({ rows, columns }) => {
    const safeTitle = String(activePreset?.label || "CAPA_Report").replace(/[^a-z0-9]+/gi, "_");
    await exportRowsToExcel(
      rows,
      columns,
      `${safeTitle}_${new Date().toISOString().split("T")[0]}.xlsx`,
      "CAPA Report",
    );
  }, [activePreset?.label, exportRowsToExcel]);

  const exportPivotToExcel = useCallback(async () => {
    const colKeys = pivot.colKeys.length ? pivot.colKeys : ["Total"];
    const rows = pivot.rowKeys.map((rk) => {
      const rowMap = pivot.matrix.get(rk);
      return {
        rowLabel: rk,
        ...Object.fromEntries(colKeys.map((ck) => [ck, rowMap?.get(ck) ?? 0])),
      };
    });
    const rowFieldLabel = getFieldMeta(builder.rows[0])?.label || "Rows";
    const columns = ["rowLabel", ...colKeys];
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Pivot");
    const headerRow = ws.addRow([rowFieldLabel, ...colKeys]);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } };
    });
    rows.forEach((row) => ws.addRow(columns.map((field) => row[field] ?? 0)));
    ws.columns = [rowFieldLabel, ...colKeys].map((header) => ({
      header,
      key: header,
      width: Math.max(14, String(header).length + 4),
    }));
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const safeTitle = String(activePreset?.label || "CAPA_Pivot").replace(/[^a-z0-9]+/gi, "_");
    downloadBlob(blob, `${safeTitle}_Pivot_${new Date().toISOString().split("T")[0]}.xlsx`);
  }, [activePreset?.label, builder.rows, pivot]);

  const exportPivotToPdf = useCallback(() => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const title = activePreset?.label || "CAPA Pivot";
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const rowHeight = 24;
    const headerHeight = 28;
    doc.setFontSize(14);
    doc.text(title, 40, 42);

    const rowFieldLabel = getFieldMeta(builder.rows[0])?.label || "Rows";
    const colKeys = pivot.colKeys.length ? pivot.colKeys : ["Total"];
    const headers = [rowFieldLabel, ...colKeys];
    const availableWidth = pageWidth - margin * 2;
    const firstColWidth = Math.min(180, Math.max(120, availableWidth * 0.28));
    const otherColWidth = (availableWidth - firstColWidth) / Math.max(colKeys.length, 1);
    const colWidths = [firstColWidth, ...colKeys.map(() => otherColWidth)];

    const drawHeader = (startY) => {
      let x = margin;
      doc.setFontSize(8);
      doc.setFont(undefined, "bold");
      headers.forEach((header, index) => {
        doc.setFillColor(68, 114, 196);
        doc.setDrawColor(209, 213, 219);
        doc.rect(x, startY, colWidths[index], headerHeight, "FD");
        doc.setTextColor(255, 255, 255);
        doc.text(String(header), x + 6, startY + 18, {
          maxWidth: colWidths[index] - 12,
        });
        x += colWidths[index];
      });
      doc.setTextColor(15, 23, 42);
      doc.setFont(undefined, "normal");
      return startY + headerHeight;
    };

    let y = drawHeader(62);
    pivot.rowKeys.forEach((rk, rowIndex) => {
      if (y + rowHeight > pageHeight - margin) {
        doc.addPage();
        doc.setFontSize(14);
        doc.text(title, margin, 42);
        y = drawHeader(62);
      }
      const rowMap = pivot.matrix.get(rk);
      const values = [rk, ...colKeys.map((ck) => rowMap?.get(ck) ?? 0)];
      let x = margin;
      doc.setFontSize(8);
      values.forEach((value, index) => {
        if (rowIndex % 2 === 0) {
          doc.setFillColor(248, 250, 252);
        } else {
          doc.setFillColor(255, 255, 255);
        }
        doc.setDrawColor(229, 231, 235);
        doc.rect(x, y, colWidths[index], rowHeight, "FD");
        doc.setTextColor(15, 23, 42);
        doc.text(String(value ?? ""), x + 6, y + 16, {
          maxWidth: colWidths[index] - 12,
        });
        x += colWidths[index];
      });
      y += rowHeight;
    });
    const safeTitle = String(title).replace(/[^a-z0-9]+/gi, "_");
    doc.save(`${safeTitle}_Pivot_${new Date().toISOString().split("T")[0]}.pdf`);
  }, [activePreset?.label, builder.rows, pivot]);

  const getChartImage = useCallback(() => {
    return chartRef.current?.toBase64Image?.("image/png", 1) || "";
  }, []);

  const exportChartImage = useCallback(() => {
    const image = getChartImage();
    if (!image) return;
    const link = document.createElement("a");
    const safeTitle = String(activePreset?.label || "CAPA_Chart").replace(/[^a-z0-9]+/gi, "_");
    link.href = image;
    link.download = `${safeTitle}_Chart_${new Date().toISOString().split("T")[0]}.png`;
    link.click();
  }, [activePreset?.label, getChartImage]);

  const exportChartPdf = useCallback(() => {
    const image = getChartImage();
    if (!image) return;
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const title = activePreset?.label || "CAPA Chart";
    doc.setFontSize(14);
    doc.text(title, 40, 38);
    doc.addImage(image, "PNG", 40, 58, 760, 430);
    const safeTitle = String(title).replace(/[^a-z0-9]+/gi, "_");
    doc.save(`${safeTitle}_Chart_${new Date().toISOString().split("T")[0]}.pdf`);
  }, [activePreset?.label, getChartImage]);

  const groupedQuickReports = useMemo(() => {
    const q = presetSearch.trim().toLowerCase();
    const filtered = QUICK_REPORTS.filter((item) => item.dataset !== "task").filter((item) => {
      if (!q) return true;
      return `${item.label} ${item.category} ${item.description}`.toLowerCase().includes(q);
    });
    return filtered.reduce((acc, item) => {
      if (!acc[item.category]) acc[item.category] = [];
      acc[item.category].push(item);
      return acc;
    }, {});
  }, [presetSearch]);

  const applyQuickReport = useCallback((key) => {
    const quickReport = QUICK_REPORTS.find((q) => q.key === key && q.dataset !== "task");
    const cfg = quickReport?.apply();
    if (!cfg) return;
    setActivePresetKey(key);
    setFilters(cfg.filters);
    setBuilder(cfg.builder);
    if (cfg.canvasView) setCanvasView(cfg.canvasView);
    if (cfg.chartType) setChartType(cfg.chartType);
  }, []);

  useEffect(() => {
    applyQuickReport("stage");
  }, [applyQuickReport]);

  const handleApplyListFilters = useCallback(() => {
    setAppliedListFilters(draftListFilters);
    setFilterOpen(false);
  }, [draftListFilters]);

  const handleClearListFilters = useCallback(() => {
    setDraftListFilters(CAPA_LIST_FILTER_INITIAL);
    setAppliedListFilters(CAPA_LIST_FILTER_INITIAL);
  }, []);

  if (!userData) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status" />
      </div>
    );
  }
  if (!canCapaReports) return <Navigate to="/notification-center" replace />;

  return (
    <div className={`capa-page capa-reports-builder ${showFiltersPanel ? "" : "is-filters-collapsed"}`}>
      <div className="capa-reports-builder__layout">
        <aside className="capa-reports-builder__sidebar capa-reports-builder__sidebar--filters">
          <div className="capa-reports-builder__filters-tab-wrap">
            <button
              type="button"
              className="capa-reports-builder__filters-tab"
              onClick={() => setShowFiltersPanel((prev) => !prev)}
              title={showFiltersPanel ? "Hide filters" : "Show filters"}
            >
              {showFiltersPanel ? (
                <i className="fas fa-chevron-right" title="Hide" />
              ) : (
                <i className="fas fa-chevron-left" title="Show" />
              )}
              <i className="fas fa-filter" />
              <span>Filters</span>
            </button>
          </div>
          <div className={`capa-reports-builder__filters-panel ${showFiltersPanel ? "" : "collapsed"}`}>
            <div className="capa-reports-builder__card">
              <div className="capa-reports-builder__body">
                <div className="capa-reports-builder__native-filters">
                <div className="capa-reports-builder__filter-field">
                  <span>Date range</span>
                  <DateRangeFilter
                    countryCode="+44"
                    value={{
                      preset: filters.dateRange,
                      startDate: filters.dateFrom,
                      endDate: filters.dateTo,
                    }}
                    onChange={(range) =>
                      setFilters((p) => ({
                        ...p,
                        dateRange: range.preset,
                        dateFrom: range.startDate,
                        dateTo: range.endDate,
                      }))
                    }
                  />
                </div>
                <div className="capa-reports-builder__filter-field">
                  <span>Stage</span>
                  <BadgeMultiSelect
                    options={options.stages}
                    value={selectedStages}
                    onChange={(values) =>
                      setFilters((prev) => ({ ...prev, stage: values.length ? values : "All" }))
                    }
                    emptyLabel="All stages"
                    popoverTitle="Add stage"
                    popoverSubtitle="Select stages to include"
                  />
                </div>
                <div className="capa-reports-builder__filter-field">
                  <span>Owner</span>
                  <BadgeMultiSelect
                    options={options.assignees}
                    value={selectedOwners}
                    onChange={(values) =>
                      setFilters((prev) => ({ ...prev, assignee: values.length ? values : "All" }))
                    }
                    emptyLabel="All owners"
                    popoverTitle="Add owner"
                    popoverSubtitle="Select owners to include"
                  />
                </div>
                <div className="capa-reports-builder__filter-field">
                  <span>Risk</span>
                  <BadgeMultiSelect
                    options={options.risks}
                    value={selectedRisks}
                    onChange={(values) =>
                      setFilters((prev) => ({ ...prev, risk: values.length ? values : "All" }))
                    }
                    emptyLabel="All risks"
                    popoverTitle="Add risk"
                    popoverSubtitle="Select risks to include"
                  />
                </div>
                <div className="capa-reports-builder__filter-field">
                  <span>Source type</span>
                  <BadgeMultiSelect
                    options={options.types}
                    value={selectedSourceTypes}
                    onChange={(values) =>
                      setFilters((prev) => ({ ...prev, type: values.length ? values : "All" }))
                    }
                    emptyLabel="All source types"
                    popoverTitle="Add source type"
                    popoverSubtitle="Select source types to include"
                  />
                </div>
                <div className="capa-reports-builder__filter-field">
                  <span>Module</span>
                  <Dropdown
                    options={options.modules}
                    selected={selectedModule}
                    onSelect={(option) =>
                      setFilters((p) => ({
                        ...p,
                        module: option?.value || "All",
                        context: "All",
                      }))
                    }
                  />
                </div>
                <div className="capa-reports-builder__filter-field">
                  <span>Context</span>
                  <Dropdown
                    isMulti
                    options={options.contexts.length ? options.contexts : [contextPlaceholderOption]}
                    selected={selectedContexts}
                    disabled={filters.module === "All"}
                    onSelect={(items) =>
                      setFilters((p) => ({
                        ...p,
                        context: Array.isArray(items) && items.length
                          ? items.map((item) => String(item.value))
                          : "All",
                      }))
                    }
                  />
                </div>
              </div>
              </div>
            </div>
          </div>
        </aside>

        <main className="capa-reports-builder__canvas-col">
          <div className="capa-reports-builder__card">
            <div className="capa-reports-builder__head capa-reports-builder__canvas-head">
              <div>
                <strong>{activePreset?.label || "Custom report"}</strong>
              </div>
              <div className="capa-reports-builder__output-actions">
                <div className="btn-group btn-group-sm">
                  <button type="button" className={`btn btn-outline-secondary ${canvasView === "table" ? "active" : ""}`} onClick={() => setCanvasView("table")}>Table</button>
                  <button type="button" className={`btn btn-outline-secondary ${canvasView === "pivot" ? "active" : ""}`} onClick={() => setCanvasView("pivot")}>Pivot</button>
                  <button type="button" className={`btn btn-outline-secondary ${canvasView === "chart" ? "active" : ""}`} onClick={() => setCanvasView("chart")}>Chart</button>
                </div>
              </div>
            </div>

            <div className="capa-reports-builder__body">
              <div className="capa-reports-builder__summary">
                {reportSummary.map((item) => (
                  <div key={item.label} className="capa-reports-builder__summary-card">
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>

              {canvasView === "table" && (
                <ReportTable
                  rows={filteredRows}
                  fieldOptions={FIELD_OPTIONS}
                  defaultColumns={DEFAULT_TABLE_COLUMNS}
                  getFieldLabel={(field) => getFieldMeta(field)?.label || field}
                  renderCell={renderTableCellValue}
                  onExportExcel={exportTableToExcel}
                />
              )}

              {canvasView === "pivot" && (
                <>
                  <div className="capa-reports-builder__component-toolbar">
                    <button type="button" onClick={exportPivotToExcel} disabled={!pivot.rowKeys.length}>
                      <i className="fas fa-file-excel" aria-hidden="true" />
                      Excel
                    </button>
                    <button type="button" onClick={exportPivotToPdf} disabled={!pivot.rowKeys.length}>
                      <i className="fas fa-file-pdf" aria-hidden="true" />
                      PDF
                    </button>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm capa-reports-builder__table capa-reports-builder__pivot-table">
                      <thead>
                        <tr>
                          <th>{getFieldMeta(builder.rows[0])?.label || "Rows"}</th>
                          {pivot.colKeys.map((ck) => (
                            <th key={ck}>{ck}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pivot.rowKeys.map((rk) => (
                          <tr key={rk}>
                            <td className="fw-semibold">{rk}</td>
                            {pivot.colKeys.map((ck) => (
                              <td key={`${rk}-${ck}`}>{pivot.matrix.get(rk)?.get(ck) ?? 0}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {canvasView === "chart" && (
                <div className="capa-reports-builder__chart">
                  <div className="capa-reports-builder__chart-toolbar">
                    <div className="capa-reports-builder__component-toolbar">
                      <button type="button" onClick={exportChartImage} disabled={!chartData.labels.length}>
                        <i className="fas fa-image" aria-hidden="true" />
                        Image
                      </button>
                      <button type="button" onClick={exportChartPdf} disabled={!chartData.labels.length}>
                        <i className="fas fa-file-pdf" aria-hidden="true" />
                        PDF
                      </button>
                    </div>
                    <div className="capa-reports-builder__chart-toggle" role="group" aria-label="Chart type">
                      <button type="button" className={chartType === "bar" ? "active" : ""} aria-pressed={chartType === "bar"} onClick={() => setChartType("bar")}>
                        Bar
                      </button>
                      <button type="button" className={chartType === "pie" ? "active" : ""} aria-pressed={chartType === "pie"} onClick={() => setChartType("pie")}>
                        Pie
                      </button>
                    </div>
                  </div>
                  <div className="capa-reports-builder__chart-canvas">
                    {chartType === "bar" ? (
                      <Bar
                        ref={chartRef}
                        options={barChartOptions}
                        data={{
                          labels: chartData.labels,
                          datasets: [
                            {
                              data: chartData.values,
                              backgroundColor: chartData.colors,
                              borderRadius: 8,
                              maxBarThickness: 66,
                              categoryPercentage: chartData.labels.length === 1 ? 0.35 : 0.72,
                              barPercentage: 0.82,
                            },
                          ],
                        }}
                      />
                    ) : (
                      <Pie
                        ref={chartRef}
                        options={pieChartOptions}
                        data={{
                          labels: chartData.labels,
                          datasets: [
                            {
                              data: chartData.values,
                              backgroundColor: chartData.colors,
                              borderColor: "#fff",
                              borderWidth: 2,
                            },
                          ],
                        }}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        <aside className="capa-reports-builder__sidebar capa-reports-builder__sidebar--reports">
          <div className="capa-reports-builder__card">
            <div className="capa-reports-builder__body">
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder="Search reports..."
                value={presetSearch}
                onChange={(e) => setPresetSearch(e.target.value)}
              />

              <div className="capa-reports-builder__report-library">
                {Object.entries(groupedQuickReports).map(([category, reports]) => (
                  <section key={category}>
                    <h3>{category}</h3>
                    {reports.map((q) => (
                      <button
                        key={q.key}
                        type="button"
                        className={`capa-reports-builder__report-card ${activePresetKey === q.key ? "active" : ""}`}
                        onClick={() => applyQuickReport(q.key)}
                      >
                        <strong>{q.label}</strong>
                        <span>{q.description}</span>
                      </button>
                    ))}
                  </section>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>

      <CapaListFilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        draftFilters={draftListFilters}
        setDraftFilters={setDraftListFilters}
        onApply={handleApplyListFilters}
        onClearFilters={handleClearListFilters}
        uniqueOwners={[]}
        userData={userData}
      />

    </div>
  );
};

export default CapaReports;
